import { useState, useEffect } from "react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface GalleryItem {
  id: string;
  title: string;
  category: string;
  type: string;
  image_url: string | null;
  video_url: string | null;
}

const Gallery = () => {
  const [filter, setFilter] = useState<"all" | "ppf" | "ceramic" | "tint" | "videos">("all");
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_items")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setGalleryItems(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading gallery",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = filter === "all" 
    ? galleryItems 
    : filter === "videos"
    ? galleryItems.filter(item => item.type === "video")
    : galleryItems.filter(item => item.category === filter);

  return (
    <div className="min-h-screen">
      <SEO
        title="Gallery | PPF & Ceramic Coating Work | Battle Born Protection Films"
        description="See completed PPF and ceramic coating installations by Battle Born Protection Films. Real Las Vegas vehicle work including luxury and exotic cars."
        canonical="https://battlebornppf.com/gallery"
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-darker-bg to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">
              Our Work
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore our portfolio of premium automotive protection installations
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Button 
              variant={filter === "all" ? "premium" : "outline"}
              onClick={() => setFilter("all")}
              size="lg"
            >
              All Projects
            </Button>
            <Button 
              variant={filter === "ppf" ? "premium" : "outline"}
              onClick={() => setFilter("ppf")}
              size="lg"
            >
              Paint Protection Film
            </Button>
            <Button 
              variant={filter === "ceramic" ? "premium" : "outline"}
              onClick={() => setFilter("ceramic")}
              size="lg"
            >
              Ceramic Coatings
            </Button>
            <Button 
              variant={filter === "tint" ? "premium" : "outline"}
              onClick={() => setFilter("tint")}
              size="lg"
            >
              Window Tint
            </Button>
            <Button 
              variant={filter === "videos" ? "premium" : "outline"}
              onClick={() => setFilter("videos")}
              size="lg"
            >
              Videos
            </Button>
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading gallery...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No items found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
              <div 
                key={item.id}
                className="group relative overflow-hidden rounded-lg border border-border bg-card aspect-[4/3] animate-fade-in"
              >
                  {item.type === "video" ? (
                    // Video embed support
                    <div className="w-full h-full">
                      <iframe
                        src={item.video_url || ""}
                        title={item.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : (
                    <>
                      <img 
                        src={item.image_url || ""} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-lg font-heading font-semibold text-white">
                            {item.title}
                          </h3>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Ready for Your Project?
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let us protect your vehicle with the same precision and care
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
